import { useState } from "react";
import { Sparkles, Copy, Check, Loader2, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PROVIDERS = [
  { key: "openai", label: "ChatGPT" },
  { key: "claude", label: "Claude" },
];

// submission kind -> admin collection route segment
const KIND_ROUTE = { contact: "contacts", volunteer: "volunteers", partner: "partners", pledge: "pledges" };

const fmtDate = (v) => { try { return new Date(v).toLocaleString(); } catch { return String(v); } };

/**
 * One-click AI drafter for a submission row. Admin can pick the AI provider,
 * edit the draft, copy it, or save it as a reply attached to the submission.
 */
export default function DraftReplyButton({ kind, submission, onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [saving, setSaving] = useState(false);
  const [replies, setReplies] = useState(submission.replies || []);

  async function run(p = provider) {
    setLoading(true);
    setOpen(true);
    try {
      const { data } = await api.post("/ai/draft-reply", { kind, submission, provider: p });
      setDraft(data.draft);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not draft a reply right now.");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }

  async function saveReply() {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/${KIND_ROUTE[kind]}/${submission.id}/replies`, {
        content: draft.trim(),
        provider,
      });
      setReplies(data.replies || []);
      if (onSaved) onSaved(data);
      toast.success("Reply saved to this submission.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the reply.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => run()}
        data-testid={`draft-reply-btn-${kind}-${submission.id}`}
        disabled={loading}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-palm hover:text-palm-deep disabled:opacity-50"
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
        Draft reply
        {replies.length > 0 && (
          <span className="bg-palm/10 text-palm rounded-full px-1.5 py-0.5 text-[10px]" data-testid={`reply-count-${submission.id}`}>
            {replies.length}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-start justify-center overflow-auto p-6" onClick={() => setOpen(false)} data-testid={`draft-reply-modal-${submission.id}`}>
          <div className="bg-white rounded-[14px] w-full max-w-[640px] p-7 mt-16" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="text-mango-deep" size={18} />
                <h3 className="font-display text-xl">AI draft · {kind} reply</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-ink/60 hover:text-berry text-sm font-bold">Close</button>
            </div>

            <div className="flex items-center gap-2 mb-4" data-testid={`provider-picker-${submission.id}`}>
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-ink/50 mr-1">AI:</span>
              {PROVIDERS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={loading}
                  onClick={() => { setProvider(p.key); run(p.key); }}
                  data-testid={`provider-${p.key}-${submission.id}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 transition ${
                    provider === p.key ? "bg-palm text-white border-palm" : "bg-white text-ink border-ink/15 hover:border-palm"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-ink/60 py-10 justify-center">
                <Loader2 className="animate-spin" size={18} /> Writing a thoughtful reply with {provider === "claude" ? "Claude" : "ChatGPT"}…
              </div>
            ) : (
              <>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={11}
                  data-testid={`draft-reply-text-${submission.id}`}
                  className="w-full rounded-md border border-ink/15 px-3 py-2.5 text-[15px] leading-[1.65] focus:outline-none focus:border-palm"
                />
                <div className="mt-4 flex flex-wrap gap-3 justify-end">
                  <button onClick={copy} data-testid={`draft-reply-copy-${submission.id}`} className="inline-flex items-center gap-2 border-2 border-ink/15 text-ink font-bold rounded-full px-5 py-2.5 hover:border-palm hover:text-palm transition">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                  <button onClick={() => run()} className="inline-flex items-center gap-2 border-2 border-ink/15 text-ink font-bold rounded-full px-5 py-2.5 hover:border-palm hover:text-palm transition">
                    <Sparkles size={14} /> Regenerate
                  </button>
                  <button onClick={saveReply} disabled={saving || !draft.trim()} data-testid={`draft-reply-save-${submission.id}`} className="inline-flex items-center gap-2 bg-palm text-white font-bold rounded-full px-5 py-2.5 hover:shadow-xl transition disabled:opacity-60">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save as reply
                  </button>
                </div>

                {replies.length > 0 && (
                  <div className="mt-6 border-t border-ink/10 pt-4" data-testid={`saved-replies-${submission.id}`}>
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-ink/50 mb-3">Saved replies ({replies.length})</div>
                    <div className="space-y-3 max-h-[220px] overflow-auto">
                      {replies.map((r, i) => (
                        <div key={i} className="bg-paper border border-ink/10 rounded-[10px] p-3">
                          <div className="text-[11px] text-ink/50 mb-1">
                            {r.provider === "manual" ? "Manual" : r.provider === "claude" ? "Claude" : r.provider === "openai" ? "ChatGPT" : r.provider}
                            {" · "}{r.created_by}{" · "}{fmtDate(r.created_at)}
                          </div>
                          <div className="text-sm whitespace-pre-wrap text-ink/85">{r.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
